import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturnType } from '@mantine/form';
import { userManagementService } from '@/api/services/UserManagementService';
import { getReadableApiMessage, getStatus } from '@/api/errors';
import { isValidInn } from '@/lib/validators';
import type { RegisterFormValues, UserType } from '@/types/auth';

type RegistryField = 'lastName' | 'firstName' | 'patronymic' | 'name';

/** ИНН, отклонённый проверкой: ошибка держится, пока ИНН тот же и новая проверка его не приняла. */
export interface InnRejection {
  readonly value: string;
  readonly message: string;
}

interface InnAutofillOptions {
  readonly form: UseFormReturnType<RegisterFormValues>;
  readonly type: UserType | '';
  readonly inn: string;
  readonly rejection: MutableRefObject<InnRejection | null>;
}

// Отказ по существу: сервер проверил ИНН и не принял его. Всё остальное (5xx, 429, обрыв) — сбой проверки, а не ответ об ИНН.
function isDefinitiveRejection(error: unknown): boolean {
  const status = getStatus(error);
  return status === 406 || (status !== undefined && status >= 200 && status < 300);
}

function registryValuesOf(data: Awaited<ReturnType<typeof userManagementService.validateInn>>) {
  const filled: Partial<Record<RegistryField, string>> = {};
  if (data.lastName) {
    filled.lastName = data.lastName;
  }
  if (data.firstName) {
    filled.firstName = data.firstName;
  }
  if (data.patronymicName) {
    filled.patronymic = data.patronymicName;
  }
  if (data.name?.trim()) {
    filled.name = data.name;
  }
  return filled;
}

/**
 * Автозаполнение формы регистрации по ИНН (FR-REG-3): когда тип выбран и ИНН валиден, подставляет данные реестра.
 * При смене ИНН убирает только подставленное и не исправленное вручную, ответ по прежнему ИНН отбрасывает. При сбое
 * проверки отправку не блокирует: сервер проверит ИНН сам (FR-REG-7), а `retry` позволяет проверить ещё раз.
 */
export function useInnAutofill({ form, type, inn, rejection }: InnAutofillOptions) {
  const { t } = useTranslation();
  const [checking, setChecking] = useState(false);
  const [nameLocked, setNameLocked] = useState(false); // организация пришла из реестра
  const [checkFailed, setCheckFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const registryValues = useRef<Partial<Record<RegistryField, string>>>({});

  useEffect(() => {
    const previous = registryValues.current;
    registryValues.current = {};
    setNameLocked(false);
    setCheckFailed(false);
    const current = form.getValues();
    (Object.keys(previous) as RegistryField[])
      .filter((field) => current[field] === previous[field])
      .forEach((field) => form.setFieldValue(field, ''));
    if (!type || !isValidInn(inn, type)) {
      setChecking(false);
      return;
    }
    setChecking(true);
    let stale = false; // ответ по уже изменённому ИНН подставил бы чужую организацию
    const handle = window.setTimeout(() => {
      userManagementService
        .validateInn({ type, inn })
        .then((data) => {
          if (stale) {
            return;
          }
          const filled = registryValuesOf(data);
          registryValues.current = filled;
          rejection.current = null;
          form.setValues(filled);
          // setValues ошибки не снимает: заполненное поле не должно показывать «Обязательное поле».
          (Object.keys(filled) as RegistryField[]).forEach((field) => form.clearFieldError(field));
          setNameLocked(Boolean(filled.name));
          form.validateField('inn'); // снимет ошибку проверки, но оставит конфликт «ИНН уже зарегистрирован»
        })
        .catch((error) => {
          if (stale) {
            return;
          }
          if (isDefinitiveRejection(error)) {
            const message = getReadableApiMessage(error) ?? t('auth.errors.innInvalid');
            rejection.current = { value: inn, message };
            form.setFieldError('inn', message);
          } else {
            setCheckFailed(true);
          }
        })
        .finally(() => {
          if (!stale) {
            setChecking(false);
          }
        });
    }, 200);
    return () => {
      stale = true;
      window.clearTimeout(handle);
    };
    // Триггеры осознанно только ИНН, тип и повтор; form и t стабильны в рамках жизни компонента.
  }, [type, inn, attempt]);

  return { checking, nameLocked, checkFailed, retry: () => setAttempt((value) => value + 1) };
}
